import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import CustomButton from '../../shared/customButton';
import EnrollCourseDialog from '../../shared/enrollCourseDialog';
import AlertDialog from '../../shared/alertDialog';
import CourseCard from '../../shared/courseCard';
import Search from '../../shared/search';
import Filters from '../../shared/filters';
import NotificationCard from '../../shared/notificationCard';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import { PLACEMENTS } from '../../../constants/tags';
import {
  APP_PAGES,
  PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  api_course_users_disenroll,
  api_course_users_enroll,
} from '../../../constants/apiRoutes';
import { sticky } from '../../../constants/helperCssRules';
import {
  showSuccessMessage,
  manageQuery,
  parameterizeQuery,
  parseDuplicateParameters,
  parseQueryParams,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import {
  hasAuthPermissionCheck,
  hasScopedAuthPermissionCheck,
} from '../../../utility/permissions';
import {
  isObjectEmpty,
  isArrayEmpty,
  trimString,
  isArray,
  isUserDeactivated,
} from '../../../utility/helpers';
import {
  prepareCourseLevels,
  prepareCourses,
  prepareEnrollmentStatuses,
  prepareCoursesToEnroll,
} from '../../../utility/courseUtils';
import http from '../../../utility/http';
import { useTranslations } from '../../../utility/useTranslations';
import { personSelector } from '../../../store/selectors/personSelector';
import { userCoursesSelector } from '../../../store/selectors/userCoursesSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { coursesSelector } from '../../../store/selectors/coursesSelector';
import { surveysSelector } from '../../../store/selectors/surveysSelector';
import {
  getCourseEnrolledUsers,
  clearCourseEnrolledUsers,
} from '../../../store/modules/courseDetails';
import {
  getUserCourses,
  updateEnrolledUserStatus,
  clearUserCourses,
  removeCourseFromUserList,
} from '../../../store/modules/userCourses';
import {
  clearAttributesWithQuestions,
  getAttributesWithQuestions,
} from '../../../store/modules/surveys';
import {
  getTagsCategories,
  clearTagsCategories,
  getPageQuickFilters,
  clearPageQuickFilters,
} from '../../../store/modules/tags';
import { getCourses } from '../../../store/modules/courses';
import { preparePageFilters, prepareCourseActions } from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  header: {
    ...sticky(primary.white, 170),
  },
  pageDescription: {
    marginBottom: spacing(4),
  },
  filtersAndActionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: spacing(4),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  search: {
    width: 200,
  },
  enrollButton: {
    marginLeft: spacing(2),
    width: 'max-content',
  },
  userCourses: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridGap: spacing(8),
  },
  disabledUserAction: {
    cursor: 'not-allowed',
  },
  disabledCourseCard: {
    cursor: 'default',
  },
}));

const { SEARCH, COURSE_LEVEL, COURSE_ATTRIBUTE, STATUS, COURSE_TAGS } = PARAMS;

const PeopleCoursesPage = ({
  auth,
  accessControl,
  organizationSettings,
  navigate,
  location,
  dispatch,
  ...rest
}) => {
  const { id } = useParams();
  const userId = (!Number.isNaN(Number(id)) && +id) || -1;

  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE_COURSES);

  const user = useSelector(personSelector);
  const { userCourses } = useSelector(userCoursesSelector);
  const { courses } = useSelector(coursesSelector);
  const { categories, pageQuickFilters } = useSelector(tagsSelector);
  const { attributesWithQuestions } = useSelector(surveysSelector);
  const allAttributes = [...(attributesWithQuestions || [])];

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [areFiltersLoading, setAreFiltersLoading] = useState(false);
  const [courseId, setCourseId] = useState(null);
  const [disenrollDialogOpened, setDisenrollDialogOpened] = useState(false);
  const [isEnrollCourseDialogOpened, setIsEnrollCourseDialogOpened] =
    useState(false);
  const [filters, setFilters] = useState({
    [COURSE_LEVEL]: [],
    [COURSE_ATTRIBUTE]: [],
    [STATUS]: [],
    [COURSE_TAGS]: [],
  });
  const [search, setSearch] = useState('');

  const handleGetCourses = useCallback(
    (currentFilters, currentSearch) => {
      const params = {
        ...currentFilters,
        ...(currentSearch ? { search: currentSearch } : {}),
      };
      const query = parseDuplicateParameters(params);

      if (location.search !== `?${query}`) {
        navigate(`/people/${userId}/learning${query ? `?${query}` : ''}`, {
          replace: true,
        });
      }

      return getUserCourses(dispatch, userId, params);
    },
    [dispatch, location.search, navigate, userId]
  );

  const getInitialData = useCallback(async () => {
    const params = parseQueryParams(
      location.search,
      PAGE_WHITELISTED_PARAMS.PEOPLE_LEARNING
    );

    try {
      if (!isObjectEmpty(params)) {
        const urlSearch = params[SEARCH]
          ? decodeURIComponent(params[SEARCH])
          : '';
        const urlFilters = {
          [COURSE_LEVEL]: params[COURSE_LEVEL] ? [params[COURSE_LEVEL]] : [],
          [COURSE_ATTRIBUTE]: params[COURSE_ATTRIBUTE]
            ? [params[COURSE_ATTRIBUTE]]
            : [],
          [STATUS]: params[STATUS] ? [params[STATUS]] : [],
          [COURSE_TAGS]: params[COURSE_TAGS]
            ? [
                ...(isArray(params[COURSE_TAGS])
                  ? params[COURSE_TAGS]
                  : [params[COURSE_TAGS]]),
              ]
            : [],
        };

        setSearch(urlSearch);
        setFilters(urlFilters);

        await Promise.all([
          handleGetCourses(urlFilters, urlSearch),
          getTagsCategories(dispatch),
          getPageQuickFilters(dispatch, PLACEMENTS.LEARNING.id),
          getAttributesWithQuestions(dispatch, {
            [PARAMS.IS_PRIVATE_ATTRIBUTE]: false,
          }),
        ]);
      } else {
        await Promise.all([
          handleGetCourses(
            {
              [COURSE_LEVEL]: [],
              [COURSE_ATTRIBUTE]: [],
              [STATUS]: [],
              [COURSE_TAGS]: [],
            },
            ''
          ),
          getTagsCategories(dispatch),
          getPageQuickFilters(dispatch, PLACEMENTS.LEARNING.id),
          getAttributesWithQuestions(dispatch, {
            [PARAMS.IS_PRIVATE_ATTRIBUTE]: false,
          }),
        ]);
      }
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch, handleGetCourses, location.search]);

  const cleanup = useCallback(() => {
    dispatch(clearUserCourses());
    dispatch(clearCourseEnrolledUsers());
    dispatch(clearAttributesWithQuestions());
    dispatch(clearTagsCategories());
    dispatch(clearPageQuickFilters());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleApplyFilters = newFilters => {
    setFilters(newFilters);
    setAreFiltersLoading(true);

    handleGetCourses(newFilters, search).then(() =>
      setAreFiltersLoading(false)
    );
  };

  const onEnrollCoursesSearch = value => {
    const text = trimString(value);

    const query = manageQuery({
      key: 'search',
      value: text,
      shouldDelete: !text,
      query: '',
    });

    return getCourses(dispatch, parameterizeQuery(query));
  };

  const onEnrolledCoursesSearch = value => {
    const searchTerm = trimString(value);

    setSearch(searchTerm);
    setAreFiltersLoading(true);

    handleGetCourses(filters, searchTerm).then(() =>
      setAreFiltersLoading(false)
    );
  };

  const onViewDetails = (canViewCourse, selectedCourseId) => () => {
    if (canViewCourse) {
      navigate(`/learning/${selectedCourseId}`);
    }
  };

  const toggleDisenrollDialog = selectedCourseId => {
    setDisenrollDialogOpened(prev => !prev);
    setCourseId(selectedCourseId || null);
  };

  const onDisenrollUser = () => {
    if (courseId && userId) {
      http
        .post(api_course_users_disenroll(courseId), {
          users: [userId],
        })
        .then(() => {
          dispatch(removeCourseFromUserList({ courseId }));
          showSuccessMessage(translations.disenrollDialog.disenrollSuccess);
        })
        .finally(() => toggleDisenrollDialog());
    }
  };

  const toggleEnrollDialog = () => setIsEnrollCourseDialogOpened(prev => !prev);

  const handleEnrollCourseDialog = () =>
    getCourses(dispatch).then(toggleEnrollDialog);

  const onEnrollCourses = course =>
    http
      .post(api_course_users_enroll(course.id), { users: [userId] })
      .then(() => {
        showSuccessMessage(translations.successMessages.enrolled);
        getUserCourses(dispatch, userId);
      })
      .finally(toggleEnrollDialog);

  const onChangeEnrollmentStatus = selectedCourseId => statusId =>
    updateEnrolledUserStatus(dispatch, {
      statusId,
      courseId: selectedCourseId,
      userId,
    }).then(() => {
      showSuccessMessage(translations.successMessages.status);
    });

  const handleGetCourseEnrolledUsers = (selectedCourseId, params) =>
    getCourseEnrolledUsers(dispatch, selectedCourseId, params);

  const canViewCourses = hasAuthPermissionCheck(
    PERMISSION_CODES.LEARNING_VIEW,
    accessControl
  );
  const canManageCourseUser = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.LEARNING_MANAGE_USERS,
    accessControl,
    auth.id,
    userId
  );
  const courseLevels = prepareCourseLevels(translations.levels).slice(1);
  const enrollmentStatuses = prepareEnrollmentStatuses(
    translations.enrollmentStatuses
  );
  const enrolledCourses = prepareCourses(
    userCourses?.results || [],
    enrollmentStatuses
  );
  const filterIsActive = hasSelectedFiltersCheck(filters);
  const isDeactivatedUser = isUserDeactivated(user);

  return (
    <PeoplePageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      location={location}
      userId={userId}
      organizationSettings={organizationSettings}
      navigate={navigate}
      dispatch={dispatch}
      shouldPassProps={false}
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.header}>
            <Typography variant="body2" className={classes.pageDescription}>
              {auth.id === userId
                ? translations.descriptionOwnProfile
                : translations.pageDescription}
            </Typography>
            <div className={classes.filtersAndActionsWrapper}>
              <Filters
                translations={translations.filters}
                selectedFilters={filters}
                filters={preparePageFilters(
                  translations,
                  courseLevels,
                  allAttributes,
                  enrollmentStatuses,
                  categories,
                  pageQuickFilters
                )}
                onApplyFilters={handleApplyFilters}
              />
              <div className={classes.actions}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={onEnrolledCoursesSearch}
                />
                {canManageCourseUser && (
                  <ConditionalTooltip
                    className={classes.disabledUserAction}
                    message={translations.disabledEnrollActionTooltip}
                    addTooltip={isDeactivatedUser}
                  >
                    <CustomButton
                      className={classes.enrollButton}
                      type="addDarkRoundedOutlined"
                      disabled={isDeactivatedUser}
                      onClick={handleEnrollCourseDialog}
                    >
                      {translations.enrollEmployeeToMoreCourses}
                    </CustomButton>
                  </ConditionalTooltip>
                )}
              </div>
            </div>
          </div>
          {isArray(enrolledCourses) && !isArrayEmpty(enrolledCourses) && (
            <div className={classes.userCourses}>
              {enrolledCourses.map(course => {
                const courseActions = prepareCourseActions(
                  translations.courseActions,
                  onViewDetails(canViewCourses, course.id),
                  () => toggleDisenrollDialog(course.id),
                  canViewCourses,
                  canManageCourseUser
                );
                return (
                  <CourseCard
                    key={`course_item_${course.id}`}
                    className={classNames({
                      [classes.disabledCourseCard]: !canViewCourses,
                    })}
                    translations={translations.courseLabels}
                    course={course}
                    levels={courseLevels}
                    statuses={enrollmentStatuses}
                    actions={courseActions}
                    isDisabled={isDeactivatedUser}
                    onChangeStatus={onChangeEnrollmentStatus(course.id)}
                    onCardClick={onViewDetails(canViewCourses, course.id)}
                    isStatusSelectable={canManageCourseUser}
                    hasCourseUrl
                  />
                );
              })}
            </div>
          )}
          <NotificationCard
            title={search || filterIsActive ? '' : translations.noResultsTitle}
            content={
              search || filterIsActive
                ? translations.noSearchResultsMessage
                : translations.noResultsMessage
            }
            shouldFade={
              !areFiltersLoading &&
              isArray(enrolledCourses) &&
              isArrayEmpty(enrolledCourses)
            }
            shouldFadeOutInstant
          />
          <EnrollCourseDialog
            translations={translations.enrollCourseDialog}
            isOpened={isEnrollCourseDialogOpened}
            levels={courseLevels}
            courses={prepareCoursesToEnroll(
              courses?.results || [],
              userCourses?.results || [],
              enrollmentStatuses
            )}
            getCourseEnrolledUsers={handleGetCourseEnrolledUsers}
            onSearch={onEnrollCoursesSearch}
            onCancel={toggleEnrollDialog}
            onEnroll={onEnrollCourses}
          />
          <AlertDialog
            translations={translations.disenrollDialog}
            isOpened={disenrollDialogOpened}
            onClose={toggleDisenrollDialog}
            onConfirm={onDisenrollUser}
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

PeopleCoursesPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(PeopleCoursesPage);
